import React, { useState, useEffect, useRef } from 'react';
import { FiUsers, FiPackage, FiTrendingUp, FiAward } from 'react-icons/fi';
import { COMPANY_INFO } from '@utils/constants';
import { useInView } from 'react-intersection-observer';

/**
 * StatsSection Component
 * Displays key statistics with animated counters
 */
const StatsSection = () => {
  const stats = [
    {
      id: 1,
      icon: FiUsers,
      value: COMPANY_INFO.totalMembers,
      suffix: '+',
      label: 'Farmer Members',
      description: 'Individual farmers served',
      color: 'primary',
    },
    {
      id: 2,
      icon: FiPackage,
      value: COMPANY_INFO.totalCooperatives,
      suffix: '',
      label: 'Cooperatives',
      description: 'Primary societies',
      color: 'secondary',
    },
    {
      id: 3,
      icon: FiTrendingUp,
      value: new Date().getFullYear() - COMPANY_INFO.yearEstablished,
      suffix: '+',
      label: 'Years of Service',
      description: 'Since 2003',
      color: 'primary',
    },
    {
      id: 4,
      icon: FiAward,
      value: 100,
      suffix: '%',
      label: 'Quality Assured',
      description: 'UNBS & GMP certified',
      color: 'secondary',
    },
  ];

  return (
    <section className="section bg-bukawa-primary text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="pattern-dots w-full h-full" />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
            Our Impact in Numbers
          </h2>
          <p className="text-lg text-white/80">
            Transforming agriculture in Eastern Uganda through collective action
            and quality products
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <StatCard key={stat.id} stat={stat} />
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * StatCard Component
 * Individual stat card with animated counter
 */
const StatCard = ({ stat }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const Icon = stat.icon;

  // Color configurations
  const colorClasses = {
    primary: {
      icon: 'bg-white/10 text-bukawa-secondary',
      glow: 'bg-bukawa-secondary',
    },
    secondary: {
      icon: 'bg-white/10 text-bukawa-secondary',
      glow: 'bg-bukawa-secondary',
    },
  };

  const colors = colorClasses[stat.color];

  // Animate counter when in view
  useEffect(() => {
    if (!inView) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepValue = stat.value / steps;
    const stepDuration = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setCount(Math.floor(stepValue * currentStep));
      } else {
        setCount(stat.value);
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [inView, stat.value]);

  return (
    <div
      ref={ref}
      className="relative group"
    >
      {/* Card */}
      <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300 h-full">
        {/* Icon */}
        <div className={`inline-flex p-4 rounded-xl ${colors.icon} mb-6 relative`}>
          <Icon className="h-8 w-8" />
          {/* Glow effect */}
          <div className={`absolute inset-0 ${colors.glow} rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity`} />
        </div>

        {/* Counter */}
        <div className="mb-2">
          <span className="text-4xl md:text-5xl font-bold text-white">
            {inView ? count.toLocaleString() : '0'}
          </span>
          <span className="text-3xl md:text-4xl font-bold text-bukawa-secondary">
            {stat.suffix}
          </span>
        </div>

        {/* Label */}
        <h3 className="text-xl font-semibold text-white mb-2">
          {stat.label}
        </h3>

        {/* Description */}
        <p className="text-sm text-white/70">
          {stat.description}
        </p>
      </div>
    </div>
  );
};

export default StatsSection;